import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard, Shield } from 'lucide-react';
import { NAV_ITEMS, LOGO_URL } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { track } from '../lib/analytics';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0A0A0B]/85 border-b border-[rgba(201,162,75,0.16)]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={LOGO_URL} alt="Talas Trading Co" className="h-9 w-9 object-contain rounded" />
          <span className="font-logo font-bold tracking-wider text-base text-[#F1ECDF] hidden sm:inline">
            TALAS <span className="gold-text">TRADING CO</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.label}
                to={item.to}
                className={`text-sm transition-colors duration-200 ${active ? 'gold-text' : 'text-[#CDBE94] hover:gold-text'}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5">
          {user ? (
            <>
              {user.isAdmin && (
                <Link
                  to="/admin"
                  data-testid="navbar-admin-link"
                  className="inline-flex items-center gap-2 text-sm gold-text hover:text-[#F1ECDF] px-3 py-2 transition-colors"
                >
                  <Shield size={14} />
                  Admin
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="navbar-dashboard-link"
                className="inline-flex items-center gap-2 text-sm text-[#F1ECDF] hover:text-[#E8C874] px-3 py-2 transition-colors"
              >
                <LayoutDashboard size={14} />
                Dashboard
              </Link>
              <button
                onClick={logout}
                className="inline-flex items-center gap-2 text-sm text-[#A99563] hover:text-[#F1ECDF] px-3 py-2 transition-colors"
              >
                <LogOut size={14} />
                Log Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="navbar-login-btn"
                onClick={() => track('login_click', { source: 'navbar' })}
                className="text-sm text-[#CDBE94] hover:text-[#F1ECDF] px-4 py-2 transition-colors"
              >
                Log In
              </Link>
              <Link
                to="/signup"
                data-testid="navbar-signup-btn"
                onClick={() => track('sign_up_click', { source: 'navbar' })}
                className="cta-sheen text-sm font-semibold px-5 py-2 bg-[#C9A24B] text-[#1a1407] hover:bg-[#E8C874] transition-colors rounded-sm"
              >
                Sign Up Free
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-[#F1ECDF]" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[rgba(201,162,75,0.16)] bg-[#0A0A0B]">
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setOpen(false)}
                className="text-sm text-[#CDBE94] hover:gold-text"
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-3 flex flex-col gap-3">
              {user ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold px-5 py-2.5 bg-[#C9A24B] text-[#1a1407] rounded-sm text-center"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={() => { logout(); setOpen(false); }}
                    className="text-sm text-[#CDBE94] px-4 py-2 text-center border border-[rgba(201,162,75,0.16)] rounded-sm"
                  >
                    Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="text-sm text-[#CDBE94] px-4 py-2 text-center border border-[rgba(201,162,75,0.16)] rounded-sm"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setOpen(false)}
                    className="text-sm font-semibold px-5 py-2.5 bg-[#C9A24B] text-[#1a1407] rounded-sm text-center"
                  >
                    Sign Up Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
